// Currencies for pay & bill rates. Every rate entry (pay tables, employee pay rate) carries an ISO
// 4217 code (`currency`); entries saved before currencies were recorded have none and are in the
// tenant's default currency (Settings → Preferences, see useDefaultCurrency). Amounts in different
// currencies are never added together or converted — totals are kept per currency.
//
// The list itself comes from the browser (Intl) rather than being kept here by hand — every
// currency in use, with its English name and symbol.

// only when the tenant's own default isn't known
export const DEFAULT_CURRENCY = "GBP";

// accounting units rather than money anyone is paid or billed in
const NOT_MONEY = ["XDR", "XSU"];
// shown first in the picker — the rest follow alphabetically
const COMMON = ["GBP", "EUR", "USD", "AED"];
// only if the browser can't list currencies itself
const FALLBACK = ["GBP", "EUR", "USD", "AED", "SAR", "QAR", "BRL", "CAD", "AUD", "NZD", "CHF", "INR", "PKR", "ZAR"];

const allCodes = () => {
    try { return Intl.supportedValuesOf("currency").filter((code) => !NOT_MONEY.includes(code)); }
    catch { return FALLBACK; }
};

let displayNames;
export const currencyName = (code) => {
    try {
        displayNames = displayNames || new Intl.DisplayNames(["en-GB"], { type: "currency" });
        return displayNames.of(code) || code;
    } catch { return code; }
};

// Unambiguous symbol — £, €, US$, CA$ (a bare "$" could be any dollar); the code itself when
// there's no symbol (AED, SAR, …).
export const currencySymbol = (code = DEFAULT_CURRENCY) => {
    try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency: code || DEFAULT_CURRENCY, currencyDisplay: "symbol" })
            .formatToParts(0).find((part) => part.type === "currency")?.value || code;
    } catch { return code || DEFAULT_CURRENCY; }
};

export const currencyOf = (rateEntry, fallback = DEFAULT_CURRENCY) => rateEntry?.currency || fallback;

// £1,234.50 / €80.00 / AED 99.00
export const formatMoney = (amount, code = DEFAULT_CURRENCY) => {
    const value = parseFloat(amount || 0) || 0;
    try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency: code || DEFAULT_CURRENCY, currencyDisplay: "symbol", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    } catch { return `${code} ${value.toFixed(2)}`; }
};

// £12.9K / €900 — for tight spaces (stat strips); the exact figure goes in a tooltip
export const formatMoneyCompact = (amount, code = DEFAULT_CURRENCY) => {
    const value = parseFloat(amount || 0) || 0;
    const big = Math.abs(value) >= 1000;
    try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency: code || DEFAULT_CURRENCY, currencyDisplay: "symbol", notation: big ? "compact" : "standard", minimumFractionDigits: 0, maximumFractionDigits: big ? 1 : 0 }).format(value);
    } catch { return `${code} ${Math.round(value)}`; }
};

// { GBP: 1200, EUR: 300 } → "£1,200.00 · €300.00" (default currency first); `empty` when there's nothing
export const formatCurrencyTotals = (totals, { compact = false, empty = null, first = DEFAULT_CURRENCY } = {}) => {
    const codes = Object.keys(totals || {}).sort((a, b) => (a === first ? -1 : b === first ? 1 : a.localeCompare(b)));
    if (codes.length === 0) return empty ?? (compact ? formatMoneyCompact(0, first) : formatMoney(0, first));
    return codes.map((code) => (compact ? formatMoneyCompact : formatMoney)(totals[code], code)).join(" · ");
};

// adds `amount` to `totals[code]` — totals kept apart per currency
export const addToCurrencyTotals = (totals, code, amount) => {
    totals[code] = (totals[code] || 0) + (parseFloat(amount) || 0);
    return totals;
};

// "£ GBP – British Pound", "AED – United Arab Emirates Dirham" — same style as the invoice
// currency picker in Settings → Preferences.
let options;
export const currencyOptions = () => {
    if (!options) {
        const codes = allCodes();
        options = [...COMMON.filter((code) => codes.includes(code)), ...codes.filter((code) => !COMMON.includes(code))]
            .map((code) => {
                const symbol = currencySymbol(code);
                return { value: code, label: `${symbol !== code ? `${symbol} ` : ""}${code} – ${currencyName(code)}` };
            });
    }
    return options;
};
